<template>
    <div class="reselect-score">
        <select v-if="interact" v-model="selectedScore">
            <option
                v-for="option in availableOptions"
                :key="option.value"
                :value="option.value"
            >
                {{ option.label }}
            </option>
        </select>
        <template v-else>{{ currentScore }}</template>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-facing-decorator'
import { Options } from '@/entities/conversation'
import { defaultCsatOptions } from '@/transforms/conversation'

@Component({
    emits: ['updatescore'],
})
export default class ReselectScore extends Vue {
    @Prop({ type: Number, default: 1 }) public score!: number
    @Prop({ type: String, default: 'nps' }) public questionType!: string
    @Prop({ type: Boolean, default: false }) public interact!: boolean
    @Prop({ type: Array, default: () => defaultCsatOptions() })
    public csatLabels!: Options

    // An issue does exist where switching from not isFiveStar to isFiveStar
    // causes a blank score to display.  But that should be a no-go from a
    // operational standpoint.
    public get selectedScore() {
        return this.score
    }

    public set selectedScore(newScore) {
        this.$emit('updatescore', { score: newScore })
    }

    public get currentScore() {
        return this.formatScore(this.score)
    }

    public get availableOptions() {
        // NPS: 0 - 10
        // FiveStar: 1 - 5
        const options: { label: string; value: string }[] = []
        switch (this.questionType) {
            case 'csat':
                for (const { key, value } of this.csatLabels) {
                    options.push({
                        label: value,
                        value: key,
                    })
                }
                return options
            case 'fivestar':
                for (let value = 1; value < 6; value++) {
                    options.push({
                        label: this.formatScore(value),
                        value: `${value}`,
                    })
                }
                return options
            default:
                for (let value = 0; value < 11; value++) {
                    options.push({
                        label: this.formatScore(value),
                        value: `${value}`,
                    })
                }
                return options
        }
    }

    public formatScore(value) {
        switch (this.questionType) {
            case 'csat': {
                const option = this.csatLabels.find(({ key }) => key == value)
                return option ? option.value : `${value}`
            }
            case 'fivestar': {
                return `${value}☆`
            }
            default: {
                return `${value}`
            }
        }
    }
}
</script>

<style lang="less">
@import '@/styles/typography.less';

.reselect-score {
    display: inline;

    select {
        background-color: transparent;
        text-decoration: white;
        color: white;
        width: auto;
        margin-right: 2px;
        font-family: @fontFamily;
        font-size: 0.75em;
        font-weight: bold;
        text-align: center;
        border-radius: 5px;
        border: 1px solid white;
        outline: 0;
        transition: 0.3s ease all;

        /* Cross-browser dropdown arrow styling - should make generic */
        /* Adapted from https://gist.github.com/vladimirsiljkovic/ef176cae54d3c4d1992907e1e0723210 */
        appearance: none; /* Future */

        /* Optional styles */
        padding: 0.3em 1.5em 0.3em 0.6em;
        background-repeat: no-repeat;
        background-position: right 5px center;
        background-size: 1em;
        background-image: url('../../assets/images/chevron-down-white.svg');

        &::-ms-expand {
            display: none;
        }

        /* IE11 displays white on white otherwise.  Setting background-color results in funky displays. */
        option {
            color: black;
        }
    }
}
</style>
